import { Button } from '@commander/ui';
import { RiSendPlaneLine } from '@remixicon/react';
import {
  type FormEvent,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { COMMANDER_GO_DEFAULTS } from '../constants.js';

interface ChatInputProps {
  onSend: (text: string) => void;
  disabled: boolean;
}

export function ChatInput({ onSend, disabled }: ChatInputProps) {
  const [input, setInput] = useState('');
  const [showError, setShowError] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const errorTimeoutRef = useRef<number | null>(null);

  // Focus on mount (panel open) and keep focus until user clicks off
  useEffect(() => {
    textareaRef.current?.focus();
    return () => {
      if (errorTimeoutRef.current) window.clearTimeout(errorTimeoutRef.current);
    };
  }, []);

  const adjustHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 132)}px`;
  }, []);

  const flashError = useCallback(() => {
    setShowError(true);
    if (errorTimeoutRef.current) window.clearTimeout(errorTimeoutRef.current);
    errorTimeoutRef.current = window.setTimeout(() => setShowError(false), 800);
  }, []);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed) return;
    if (disabled) {
      flashError();
      return;
    }
    onSend(trimmed);
    setInput('');
    requestAnimationFrame(() => {
      adjustHeight();
      textareaRef.current?.focus();
    });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="border-t px-6 py-4">
      <div
        className={
          showError
            ? 'flex flex-col rounded-2xl border border-destructive bg-background ring-2 ring-destructive/30 transition-shadow'
            : 'flex flex-col rounded-2xl border border-input bg-background transition-shadow has-[textarea:focus-visible]:ring-2 has-[textarea:focus-visible]:ring-ring/20'
        }
      >
        <textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            requestAnimationFrame(adjustHeight);
          }}
          onKeyDown={handleKeyDown}
          placeholder={COMMANDER_GO_DEFAULTS.placeholder}
          rows={1}
          className="min-h-[40px] max-h-[132px] w-full resize-none overflow-y-auto bg-transparent px-4 pt-3 pb-2 text-sm outline-none placeholder:text-muted-foreground focus-visible:ring-0"
        />
        <div className="flex items-center justify-end px-2 pb-2">
          <Button
            type="submit"
            size="icon"
            disabled={disabled || !input.trim()}
            className="size-9 shrink-0 rounded-full transition-opacity duration-150"
          >
            <RiSendPlaneLine className="h-5 w-5" />
            <span className="sr-only">Send</span>
          </Button>
        </div>
      </div>
      <p className="mt-2 hidden text-center text-xs text-muted-foreground md:block">
        <kbd className="rounded border border-border px-1.5 py-0.5 text-xs">↵</kbd> to send
        <span className="mx-2">·</span>
        <kbd className="rounded border border-border px-1.5 py-0.5 text-xs">⇧ ↵</kbd> for new line
      </p>
    </form>
  );
}
